import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PayPalEnroll } from "./PayPalEnroll";

const errOf = (e, f) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return d?.message || (d?.square?.[0]?.detail) || f;
};

export function BoxTermsPanel({ terms }) {
  const rows = [["Billing", terms.frequency], ["First charge", terms.first_charge], ["Shipping", terms.shipping_fee],
    ["Ships to", terms.destinations], ["Tax", terms.tax === "included_in_price" ? "Included in price" : "Not taxed"],
    ["Cancellation", terms.cancellation]];
  return (
    <dl className="glass-panel p-4 mb-6 text-xs space-y-1.5" data-testid="box-terms-panel">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[110px_1fr] gap-2">
          <dt className="font-bebas uppercase tracking-[0.18em] text-white/50">{k}</dt><dd className="text-white/80">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function useSquareCard(sq) {
  const [card, setCard] = useState(null);
  const [err, setErr] = useState("");
  const mounted = useRef(false);
  useEffect(() => {
    if (!sq || mounted.current) return;
    mounted.current = true;
    const boot = async () => {
      try {
        const payments = window.Square.payments(sq.application_id, sq.location_id);
        const c = await payments.card();
        await c.attach("#mbx-card");
        setCard(c);
      } catch { setErr("Secure card form could not load. Please refresh."); }
    };
    if (window.Square) { boot(); return; }
    const s = document.createElement("script");
    s.src = sq.sdk_url; s.async = true; s.onload = boot;
    s.onerror = () => setErr("Secure card form could not load. Please refresh.");
    document.body.appendChild(s);
  }, [sq]);
  return { card, err };
}

export function SubscriptionEnroll({ provider, planKey, planLabel }) {
  if (provider === "paypal") return <PayPalEnroll planKey={planKey} planLabel={planLabel} />;
  return <SquareEnroll planKey={planKey} planLabel={planLabel} />;
}

function SquareEnroll({ planKey, planLabel }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ given_name: "", family_name: "", postal_code: "", consent: false });
  const [st, setSt] = useState({ kind: "idle", msg: "" });
  useEffect(() => { api.get("/subscriptions/plans").then(({ data }) => setInfo(data)).catch(() => setInfo(null)); }, []);
  const { card, err } = useSquareCard(user && info?.enrollment_open ? info.square : null);
  const plan = info?.plans?.find((p) => p.key === planKey);

  if (!user) {
    return (
      <div className="text-center" data-testid="enroll-login-required">
        <h2 className="font-anton uppercase text-3xl text-white">Subscribe</h2>
        <p className="text-white/60 text-sm mt-2">Log in so you can manage or cancel your box from your account.</p>
        <Link to="/auth/login?next=/lifted-monthly-box" className="btn-lifted mt-5 inline-flex" data-testid="enroll-login-btn">Log in to subscribe</Link>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    if (!f.given_name || !f.postal_code) return setSt({ kind: "err", msg: "Name and billing ZIP are required." });
    if (!f.consent) return setSt({ kind: "err", msg: "Please agree to the recurring billing terms." });
    setSt({ kind: "loading", msg: "" });
    try {
      const res = await card.tokenize({ intent: "STORE", customerInitiated: true, sellerKeyedIn: false,
        billingContact: { givenName: f.given_name, familyName: f.family_name, email: user.email, postalCode: f.postal_code, countryCode: "US" } });
      if (res.status !== "OK") throw new Error("Card was not accepted. Check the details and try again.");
      const { data } = await api.post("/subscriptions/create", { ...f, plan_key: planKey, source_id: res.token, email: user.email });
      setSt({ kind: "ok", msg: `Subscribed (${data.status}). ${data.note}` });
    } catch (e2) { setSt({ kind: "err", msg: e2.message && !e2.response ? e2.message : errOf(e2, "Enrollment failed. You were not charged by Lifted.") }); }
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="subscription-enroll-form">
      <h2 className="font-anton uppercase text-3xl text-white text-center">Subscribe Now</h2>
      <div className="text-center text-sm text-white/70" data-testid="enroll-plan-summary">
        {planLabel} — <span className="text-lifted-green">${((plan?.total_cents || 0) / 100).toFixed(2)}/month</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className="input-lifted" placeholder="First name" value={f.given_name} onChange={(e) => setF({ ...f, given_name: e.target.value })} data-testid="enroll-given-name" />
        <input className="input-lifted" placeholder="Last name" value={f.family_name} onChange={(e) => setF({ ...f, family_name: e.target.value })} data-testid="enroll-family-name" />
      </div>
      <input className="input-lifted w-full" placeholder="Billing ZIP" value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} data-testid="enroll-postal" />
      <div id="mbx-card" className="min-h-[90px] bg-white/5 rounded p-2" data-testid="enroll-card-container" />
      {err && <div className="text-xs text-rose-300">{err}</div>}
      <label className="flex gap-2 text-xs text-white/75">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} data-testid="enroll-consent" />
        I authorize Lifted to save this card with Square and charge ${((plan?.total_cents || 0) / 100).toFixed(2)} every month until I cancel, per the terms above.
      </label>
      <button disabled={!card || st.kind === "loading"} className="btn-lifted w-full disabled:opacity-50 inline-flex justify-center gap-2" data-testid="enroll-submit">
        {st.kind === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Start subscription
      </button>
      {st.kind === "ok" && <div className="text-sm text-lifted-green flex gap-2" data-testid="enroll-success"><ShieldCheck className="h-4 w-4" />{st.msg} <Link to="/account/monthly-box" className="underline">Manage</Link></div>}
      {st.kind === "err" && <div className="text-sm text-rose-300" data-testid="enroll-error">{st.msg}</div>}
    </form>
  );
}
